/**
 * helpers.js — JavaScript mirror of src/backend/engine/helpers.py
 *
 * Injected into expression preview evaluation so users see resolved values
 * without a network round-trip. Must stay in sync with the Python module.
 */

// ── strftime polyfill ─────────────────────────────────────────────────────

const MONTH_LONG  = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const MONTH_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const DAY_LONG    = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
const DAY_SHORT   = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

function pad(n, w = 2) { return String(n).padStart(w, '0'); }

function strftime(fmt, dt) {
  if (!(dt instanceof Date)) dt = new Date(dt);
  return fmt.replace(/%[A-Za-z%]/g, tok => {
    switch (tok) {
      case '%Y': return dt.getFullYear();
      case '%y': return String(dt.getFullYear()).slice(-2);
      case '%m': return pad(dt.getMonth() + 1);
      case '%d': return pad(dt.getDate());
      case '%H': return pad(dt.getHours());
      case '%I': return pad(dt.getHours() % 12 || 12);
      case '%M': return pad(dt.getMinutes());
      case '%S': return pad(dt.getSeconds());
      case '%f': return pad(dt.getMilliseconds() * 1000, 6);
      case '%A': return DAY_LONG[dt.getDay()];
      case '%a': return DAY_SHORT[dt.getDay()];
      case '%B': return MONTH_LONG[dt.getMonth()];
      case '%b': return MONTH_SHORT[dt.getMonth()];
      case '%p': return dt.getHours() < 12 ? 'AM' : 'PM';
      case '%j': {
        const start = new Date(dt.getFullYear(), 0, 0);
        return pad(Math.floor((dt - start) / 86400000), 3);
      }
      case '%Z': return 'UTC';
      case '%%': return '%';
      default:   return tok;
    }
  });
}

function coerceDate(v) {
  if (v instanceof Date) return v;
  if (typeof v === 'string' || typeof v === 'number') {
    const d = new Date(v);
    return isNaN(d) ? null : d;
  }
  return null;
}

// ── Helpers object ────────────────────────────────────────────────────────

const helpers = {

  // ── String ────────────────────────────────────────────────────────────────

  lower(s)                  { return String(s ?? '').toLowerCase(); },
  upper(s)                  { return String(s ?? '').toUpperCase(); },
  trim(s)                   { return String(s ?? '').trim(); },
  replace(s, old, nw)       { return String(s ?? '').split(old).join(nw); },
  split(s, sep)             { return String(s ?? '').split(sep); },
  join(list, sep = '')      { return (list ?? []).map(String).join(sep); },
  contains(s, sub)          { return String(s ?? '').includes(sub); },
  starts_with(s, prefix)    { return String(s ?? '').startsWith(prefix); },
  ends_with(s, suffix)      { return String(s ?? '').endsWith(suffix); },
  substring(s, start, end)  { return String(s ?? '').slice(start, end); },
  pad_left(s, w, ch = ' ')  { return String(s ?? '').padStart(w, ch[0] ?? ' '); },
  pad_right(s, w, ch = ' ') { return String(s ?? '').padEnd(w, ch[0] ?? ' '); },
  md5(s) {
    // Browser-safe FNV-1a 32-bit as a hex stand-in (true MD5 not available without SubtleCrypto async)
    let h = 2166136261;
    for (const c of String(s ?? '')) { h ^= c.charCodeAt(0); h = (h * 16777619) >>> 0; }
    return h.toString(16).padStart(8, '0');
  },
  base64_encode(s) { try { return btoa(unescape(encodeURIComponent(String(s ?? '')))); } catch { return ''; } },
  base64_decode(s) { try { return decodeURIComponent(escape(atob(String(s ?? '')))); } catch { return ''; } },
  url_encode(s)    { return encodeURIComponent(String(s ?? '')); },
  url_decode(s)    { return decodeURIComponent(String(s ?? '')); },

  // ── Number ────────────────────────────────────────────────────────────────

  round(n, dp = 0)          { const f = Math.pow(10, dp); return Math.round(Number(n) * f) / f; },
  abs(n)                    { return Math.abs(Number(n)); },
  ceil(n)                   { return Math.ceil(Number(n)); },
  floor(n)                  { return Math.floor(Number(n)); },
  min(list)                 { const a = (list ?? []).filter(x => x != null).map(Number); return a.length ? Math.min(...a) : null; },
  max(list)                 { const a = (list ?? []).filter(x => x != null).map(Number); return a.length ? Math.max(...a) : null; },
  sum(list)                 { return (list ?? []).filter(x => x != null).reduce((acc, x) => acc + Number(x), 0); },
  avg(list) {
    const a = (list ?? []).filter(x => x != null);
    return a.length ? a.reduce((acc, x) => acc + Number(x), 0) / a.length : null;
  },
  format_number(n, fmt = ',.2f') {
    const num = Number(n);
    if (isNaN(num)) return String(n);
    // Support Python-style format specs: [,][.N][f|d|%]
    const m = fmt.match(/^([,]?)\.?(\d+)?([fde%]?)$/);
    if (!m) return String(num);
    const [, comma, dpStr, type] = m;
    const dp = dpStr !== undefined ? parseInt(dpStr) : (type === '%' ? 1 : 2);
    const val = type === '%' ? (num * 100).toFixed(dp) + '%' : num.toFixed(dp);
    if (!comma) return val;
    const parts = val.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  },

  // ── Date / Time ───────────────────────────────────────────────────────────

  now()             { return new Date(); },
  today()           { const d = new Date(); d.setHours(0,0,0,0); return d; },
  add_days(dt, n)   { const d = coerceDate(dt); if (!d) return dt; d.setDate(d.getDate() + Number(n)); return d; },
  add_hours(dt, n)  { const d = coerceDate(dt); if (!d) return dt; d.setHours(d.getHours() + Number(n)); return d; },
  add_minutes(dt,n) { const d = coerceDate(dt); if (!d) return dt; d.setMinutes(d.getMinutes() + Number(n)); return d; },
  format_date(dt, fmt = '%Y-%m-%d') {
    const d = coerceDate(dt instanceof Date ? dt : dt);
    return d ? strftime(fmt, d) : String(dt);
  },
  parse_date(s, fmt) {
    // Best-effort: try ISO first, then common formats
    const d = new Date(String(s));
    return isNaN(d) ? null : d;
  },
  diff_days(a, b) {
    const da = coerceDate(a), db = coerceDate(b);
    if (!da || !db) return null;
    return Math.round((da - db) / 86400000);
  },
  timestamp(dt) {
    const d = coerceDate(dt);
    return d ? d.getTime() / 1000 : null;
  },

  // ── Array ─────────────────────────────────────────────────────────────────

  first(list)                  { return (list ?? [])[0] ?? null; },
  last(list)                   { return list && list.length ? list[list.length - 1] : null; },
  length(val)                  { return val == null ? 0 : (val.length ?? Object.keys(val).length ?? 0); },
  slice(val, start, end)       { return val != null ? val.slice(start, end) : val; },
  filter(list, key, value)     { return (list ?? []).filter(item => item && item[key] === value); },
  map(list, key)               { return (list ?? []).map(item => (item && typeof item === 'object') ? item[key] : undefined); },
  unique(list, key) {
    const seen = new Set();
    return (list ?? []).filter(item => {
      const v = key ? (item?.[key]) : item;
      const k = typeof v === 'object' ? JSON.stringify(v) : v;
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  },
  flatten(list) {
    return (list ?? []).reduce((acc, item) => acc.concat(Array.isArray(item) ? item : [item]), []);
  },
  sort(list, key, desc = false) {
    const arr = [...(list ?? [])];
    arr.sort((a, b) => {
      const av = key ? a?.[key] : a;
      const bv = key ? b?.[key] : b;
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return av < bv ? -1 : av > bv ? 1 : 0;
    });
    return desc ? arr.reverse() : arr;
  },
  count(list, value) { return (list ?? []).filter(x => x === value).length; },

  // ── JSON / Object ─────────────────────────────────────────────────────────

  get(obj, path, def = null) {
    try {
      const parts = String(path).split('.');
      let cur = obj;
      for (const p of parts) {
        if (cur == null) return def;
        cur = Array.isArray(cur) ? cur[parseInt(p)] : cur[p];
      }
      return cur ?? def;
    } catch { return def; }
  },
  keys(obj)           { return obj && typeof obj === 'object' ? Object.keys(obj) : []; },
  values(obj)         { return obj && typeof obj === 'object' ? Object.values(obj) : []; },
  merge(a, b)         { return { ...(a ?? {}), ...(b ?? {}) }; },
  set(obj, key, val)  { return { ...(obj ?? {}), [key]: val }; },
  omit(obj, ...keys)  { const r = { ...(obj ?? {}) }; keys.forEach(k => delete r[k]); return r; },
  pick(obj, ...keys)  { const r = {}; keys.forEach(k => { if (k in (obj ?? {})) r[k] = obj[k]; }); return r; },

  // ── Conditional ───────────────────────────────────────────────────────────

  if_empty(val, fallback) {
    return (val == null || val === '' || (Array.isArray(val) && val.length === 0) ||
            (typeof val === 'object' && !Array.isArray(val) && Object.keys(val).length === 0))
      ? fallback : val;
  },
  coalesce(...vals) { return vals.find(v => v != null && v !== '') ?? null; },
  if_else(condition, yes, no) { return condition ? yes : no; },
};

// ── Template evaluator ────────────────────────────────────────────────────

/**
 * Resolve all {{ expr }} blocks in a template string using the provided context.
 * Returns { resolved: string, ok: boolean }.
 * On any evaluation error the raw template is returned with ok=false.
 */
export function resolveTemplatePreview(template, { nodes = {}, env = {}, now = new Date() } = {}) {
  if (!template || !template.includes('{{')) return { resolved: template, ok: true };

  const JSON_helper = { stringify: (v) => JSON.stringify(v, null, 2), parse: JSON.parse };

  try {
    const resolved = template.replace(/\{\{([\s\S]*?)\}\}/g, (_, expr) => {
      try {
        // eslint-disable-next-line no-new-func
        const fn = new Function('nodes', 'env', 'helpers', 'now', 'JSON',
          `"use strict"; return (${expr.trim()});`);
        const val = fn(nodes, env, helpers, now, JSON_helper);
        if (val instanceof Date) return val.toISOString();
        if (val == null) return '';
        if (typeof val === 'object') return JSON.stringify(val);
        return String(val);
      } catch {
        return `{{${expr}}}`;
      }
    });
    return { resolved, ok: true };
  } catch {
    return { resolved: template, ok: false };
  }
}

export default helpers;
